import { ActivityNode, ActivityPlanningCards, ActivityTemplate } from './types';

export const postBiz = async (payload: any) => {
  const res = await fetch('/biz', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await res.json();
  if (!res.ok || json.failure) throw new Error(json.em || json.message || 'Request failed');
  return json;
};

const rows = (json: any) => {
  const sd = json.sd || json.result?.sd || json.data?.sd;
  return Array.isArray(sd) ? sd : [];
};

export const normalizeActivityNode = (r: any): ActivityNode => ({
  nid: r.nid || `${r.nodetype || 'NODE'}-${r.rid || r.recid}`,
  pid: r.pid || null,
  rid: Number(r.rid || r.recid || 0),
  prid: r.prid ?? null,
  kpiid: r.kpiid ?? null,
  activityid: r.activityid ?? null,
  code: r.code || r.shortcode || '',
  shortcode: r.shortcode || r.code || '',
  name: r.name || r.recname || '',
  recname: r.recname || r.name || '',
  description: r.description || '',
  nodetype: r.nodetype || r.nty || 'NODE',
  levelno: Number(r.levelno || 0),
  axiscode: r.axiscode || '',
  axisname: r.axisname || '',
  objectivecode: r.objectivecode || '',
  objectivename: r.objectivename || '',
  costcentercode: r.costcentercode || '',
  costcentername: r.costcentername || '',
  status: r.status || '',
  contributionweight: r.contributionweight ?? null,
  taskweight: r.taskweight ?? null,
  totalamount: Number(r.totalamount || 0),
  progress: Number(r.progress || 0),
  raw: r,
});

export const fetchActivityTree = async (filters: any): Promise<ActivityNode[]> => {
  const json = await postBiz({ s: 'activityplanning', a: 'tree', ...filters });
  return rows(json).map(normalizeActivityNode);
};

export const fetchTasks = async (activityId: number) => {
  const json = await postBiz({ s: 'activityplanning', a: 'tasks', rid: activityId });
  return rows(json);
};

export const fetchBudgetLines = async (taskId: number | null, activityId: number | null) => {
  const json = await postBiz({ s: 'activityplanning', a: 'budgetlines', tid: taskId, aid: activityId });
  return rows(json);
};

export const fetchPlanningCards = async (activityId: number | null): Promise<ActivityPlanningCards> => {
  const json = await postBiz({ s: 'activityplanning', a: 'cards', rid: activityId });
  return json.sd || json.result || json;
};

export const fetchTemplates = async (): Promise<ActivityTemplate[]> => {
  const json = await postBiz({ s: 'activityplanning', a: 'templates' });
  return rows(json);
};

export const saveTasks = async (activityId: number, records: any[]) =>
  postBiz({ s: 'activityplanning', a: 'tasksave', rid: activityId, rcd: records });

export const saveBudgetLines = async (activityId: number, taskId: number | null, records: any[]) =>
  postBiz({ s: 'activityplanning', a: 'budgetsave', aid: activityId, tid: taskId, rcd: records });

export const applyTemplate = async (activityId: number, templateCode: string) =>
  postBiz({ s: 'activityplanning', a: 'applytemplate', rid: activityId, tpc: templateCode });

export const fetchPlanningPreview = async (activityId: number) => {
  const json = await postBiz({ s: 'activityplanning', a: 'preview', rid: activityId });
  return json.sd || json.result || json;
};

export const submitActivityPlanning = async (activityId: number, remarks = '') =>
  postBiz({ s: 'activityplanning', a: 'submit', rid: activityId, rmk: remarks });

export const addPlanningActivity = (
  kpi: number,
  nam: string,
  shc: string,
  ccc: string,
  wgt: number,
  dsc = '',
) => postBiz({
  s: 'activityplanning',
  a: 'addactivity',
  kpi,
  nam,
  shc,
  ccc,
  wgt,
  dsc,
});

export const addPlanningTask = (
  pai: number,
  nam: string,
  shc: string,
  wgt: number,
  dsc = '',
) => postBiz({
  s: 'activityplanning',
  a: 'addtask',
  pai,
  nam,
  shc,
  wgt,
  dsc,
});


// export const normalizeTaskRow = (r: any) => ({
//   id: r.rid || r.recid || r.id || r.shortcode || `tmp-${Date.now()}`,
//   rid: r.rid || r.recid || null,
//   code: r.tsc || r.tsk || r.code || r.shortcode || '',
//   name: r.nam || r.name || r.recname || '',
//   description: r.dsc || r.description || '',
//   weight: Number(r.wgt || r.weight || r.taskweight || 1),
//   status: String(r.sts || r.status || '1'),
//   raw: r,
// });

export const normalizeTaskRow = (r: any) => ({
  id: r.id ?? r.taskid ?? r.rid ?? r.activityid ?? r.recid,
  rid: r.rid ?? r.taskid ?? r.activityid ?? r.recid ?? null,

  code: r.code ?? r.taskcode ?? r.activitycode ?? r.shc ?? r.shortcode ?? '',
  name: r.name ?? r.taskname ?? r.activityname ?? r.nam ?? r.recname ?? '',
  description: r.description ?? r.taskdescription ?? r.dsc ?? '',

  weight: Number(r.weight ?? r.taskweight ?? r.wgt ?? 1),
  status: String(r.status ?? r.sts ?? r.ast ?? '1'),

  taskid: r.taskid ?? r.rid ?? null,
  activityid: r.activityid ?? null,
  costcentercode: r.costcentercode ?? '',
  raw: r,
});

export const normalizeBudgetLineRow = (r: any) => ({
  id: r.rid || r.recid || r.id || `tmp-${Date.now()}-${Math.random()}`,
  rid: r.rid || r.recid || null,
  taskid: r.tki || r.taskid || null,
  activityid: r.aci || r.activityid || null,
  accountcode: r.acc || r.accountcode || '',
  description: r.dsc || r.description || '',
  quantity: Number(r.qty || r.quantity || 0),
  unitprice: Number(r.upr || r.unitprice || 0),
  amount: Number(r.amt || r.amount || 0),
  remarks: r.rmk || r.remarks || '',
  raw: r,
});

export const fetchTasksNormalized = async (activityId: number) =>
  (await fetchTasks(activityId)).map(normalizeTaskRow);

export const fetchBudgetLinesNormalized = async (taskId: number | null, activityId: number | null) =>
  (await fetchBudgetLines(taskId, activityId)).map(normalizeBudgetLineRow);


export const fetchActivityPlan = async (activityId: number | null) => {
  const json = await postBiz({ s: 'activityplan', a: 'find', rid: activityId });
  return json.sd || json.result || json;
};

export const saveActivityPlan = async (activityId: number, rows:any[]) => {
  const json = await postBiz({ s: 'activityplan', a: 'save', rid: activityId, rows });
  return json.sd || json.result || json;
};

export const fetchIndicatorPlan = async (indicatorId: number | null, costCenterCode: string | null) => {
  const json = await postBiz({ s: 'kpiplan', a: 'find', rid: indicatorId, ccc: costCenterCode });
  return json.sd || json.result || json;
};

export const saveIndicatorPlan = async (indicatorId: number, costCenterCode: string | null, rows:any[]) => {
  const json = await postBiz({ s: 'kpiplan', a: 'save', rid: indicatorId, ccc: costCenterCode, rows });
  return json.sd || json.result || json;
};
